import type { fabric } from 'fabric';
import type { FabricEditor } from 'modules/fabric-editor';
import { isGradientFill, isPixelGradient, type LinearGradientModel } from 'shared/fabric';
import { getKindProfile } from './kind-profiles';
import type { CapturedStyle, StyleCategoryId, StyleField } from './types';

export interface ApplyStyleResult {
  /** How many targets were offered the style. */
  targets: number;
  /** How many targets actually received at least one field. */
  touched: number;
}

/**
 * Apply the captured style onto each target, restricted to `categoryIds`.
 * Kind-aware: each target's own profile decides which captured keys it can
 * accept and where they are written (Fabric prop vs `obj.data`), so cross-kind
 * paste transfers exactly the intersection of field keys. Placeholder targets
 * are refit after their data changes. The whole paste runs inside
 * `history.batch`, producing a single undo step.
 */
export function applyStyle(
  editor: FabricEditor,
  targets: fabric.Object[],
  captured: CapturedStyle,
  categoryIds: StyleCategoryId[],
): ApplyStyleResult {
  const run = (): ApplyStyleResult => {
    let touched = 0;
    for (const target of targets) {
      const profile = getKindProfile(target);
      if (!profile) continue;

      let changed = false;
      let wroteData = false;
      // `width` / `height` props are written after every category, so a gradient
      // pasted in the same go is already in `fill` and gets rebuilt for the new box.
      const size: { width?: number; height?: number } = {};
      for (const categoryId of categoryIds) {
        const fields: StyleField[] | undefined = profile[categoryId];
        const bucket = captured.values[categoryId];
        if (!fields || !bucket) continue;

        // Handle the gradient field first and on its own: the façade rebuilds the
        // captured angle + stops at the target's own size. Route the raw `fill`
        // through `setProp` instead and the target would share the source's live
        // `fabric.Gradient` by reference, in the source's pixel coordinates — on a
        // shape of another size it would not cover it. When the source had no
        // gradient, a gradient already on the target is removed (the plain `fill`
        // below then paints it); a solid target is left to that `fill` alone.
        const gradientField = fields.find((f) => f.storage === 'fillGradient');
        if (gradientField) {
          const model = bucket[gradientField.key] as LinearGradientModel | undefined;
          if (model || isGradientFill(target.fill)) {
            editor.fillGradient?.set(target, model);
            changed = true;
          }
        }

        for (const field of fields) {
          if (field.storage === 'fillGradient') continue; // handled above
          if (!(field.key in bucket)) continue;
          const value = bucket[field.key];
          if (field.storage === 'prop' && (field.key === 'width' || field.key === 'height')) {
            size[field.key] = value as number;
          } else if (field.storage === 'prop') {
            editor.objects.setProp(target, field.key as keyof fabric.Object, value as never);
          } else {
            editor.objects.setData(target, field.key, value);
            wroteData = true;
          }
          changed = true;
        }
      }

      if (size.width !== undefined || size.height !== undefined) {
        // A pixel gradient is in object coordinates — the façade rebuilds it for the
        // new box in the same write. Anything else keeps the plain prop writes.
        if (editor.fillGradient && isPixelGradient(target.fill)) {
          editor.fillGradient.setSize(target, size);
        } else {
          if (size.width !== undefined) editor.objects.setProp(target, 'width', size.width);
          if (size.height !== undefined) editor.objects.setProp(target, 'height', size.height);
        }
      }

      if (!changed) continue;
      touched++;
      // Placeholder visuals derive from data — rebuild the group. refit's own
      // transaction nests inside the surrounding batch (shared suspend counter).
      if (editor.placeholders?.isPlaceholder(target)) {
        editor.placeholders.refit(target as fabric.Group);
      } else if (wroteData) {
        // Plain cached objects (e.g. a native rect receiving `data.cornerRadii`)
        // do not repaint on a data-only write — invalidate the cache and redraw.
        target.dirty = true;
        editor.canvas.requestRenderAll();
      }
    }
    return { targets: targets.length, touched };
  };

  return editor.history ? editor.history.batch(run) : run();
}
